/**
 * Configuration Mapbox pour le marché marocain
 * Inclut les paramètres géopolitiques appropriés
 */

import { TRIP_MAP } from './constants';

// Token Mapbox (à remplacer par votre token de production)
export const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

// Configuration géopolitique pour le Maroc
export const MOROCCO_WORLDVIEW = 'MA';

// Centres géographiques du Maroc
export const MOROCCO_CENTERS = {
  casablanca: { lat: 33.5731, lng: -7.5898 },
  rabat: { lat: 33.9716, lng: -6.8498 },
  marrakech: { lat: 31.6295, lng: -7.9811 },
  fes: { lat: 34.0181, lng: -5.0078 },
  tangier: { lat: 35.7595, lng: -5.834 },
};

// Fonction pour ajouter le worldview aux requêtes Mapbox
export const createMapboxTransformRequest = () => {
  return (url, resourceType) => {
    // Ajouter le paramètre worldview pour la perspective marocaine
    if (resourceType === 'Tile' && url.includes('mapbox.com')) {
      const separator = url.includes('?') ? '&' : '?';
      return {
        url: `${url}${separator}worldview=${MOROCCO_WORLDVIEW}`,
      };
    }
    return { url };
  };
};

// Configuration par défaut pour les cartes Mapbox au Maroc
export const DEFAULT_MAPBOX_CONFIG = {
  style: 'mapbox://styles/mapbox/streets-v12',
  center: MOROCCO_CENTERS.casablanca,
  zoom: 12,
  transformRequest: createMapboxTransformRequest(),
};

// Fonction pour créer une URL d'image statique avec worldview
export const createStaticMapUrl = (center, zoom = 12, size = '800x600', style = 'satellite-v9', options = {}) => {
  const { lat, lng } = center;
  const { marker = false, markerColor = '2563eb', hideAttribution = false, hideLogo = false } = options;

  const cleanMarkerColor = String(markerColor).replace('#', '');
  const overlay = marker ? `pin-s+${cleanMarkerColor}(${lng},${lat})/` : '';

  let url = `https://api.mapbox.com/styles/v1/mapbox/${style}/static/${overlay}${lng},${lat},${zoom},0/${size}@2x?access_token=${MAPBOX_TOKEN}&worldview=${MOROCCO_WORLDVIEW}`;
  if (hideAttribution) url += '&attribution=false';
  if (hideLogo) url += '&logo=false';
  return url;
};

// Encode une liste de points en Google Polyline Algorithm (format attendu par l'API path Mapbox).
// Beaucoup plus compact que GeoJSON : ~4 chars/point vs ~40 chars/point en JSON.
const encodePolyline = (pts) => {
  let out = '',
    prevLat = 0,
    prevLng = 0;
  for (const { lat, lng } of pts) {
    const dlat = Math.round(lat * 1e5) - Math.round(prevLat * 1e5);
    const dlng = Math.round(lng * 1e5) - Math.round(prevLng * 1e5);
    prevLat = lat;
    prevLng = lng;
    for (const v0 of [dlat, dlng]) {
      let v = v0 < 0 ? ~(v0 << 1) : v0 << 1;
      while (v >= 0x20) {
        out += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
        v >>= 5;
      }
      out += String.fromCharCode(v + 63);
    }
  }
  return out;
};

/**
 * Génère une URL Mapbox Static API pour afficher la trace GPS d'un trajet.
 *
 * Utilise le format path+polyline (Google Polyline Algorithm) au lieu de GeoJSON
 * pour rester sous la limite Mapbox de 8192 chars même sur de longs trajets.
 *
 * Polyligne colorée par vitesse (seuils dans TRIP_MAP constants) :
 *   ≤ SPEED_SLOW_KMH   → vert   (bus ralenti / à l'arrêt)
 *   ≤ SPEED_MEDIUM_KMH → orange (circulation normale)
 *   > SPEED_MEDIUM_KMH → rouge  (vitesse élevée)
 */
export const createTripMapUrl = (positions, stops = [], size = '800x450') => {
  if (!positions || positions.length === 0) {
    console.debug('[MAPBOX][DEBUG] createTripMapUrl — positions vide ou null');
    return '';
  }
  console.debug(
    `[MAPBOX][DEBUG] createTripMapUrl — ${positions.length} positions, ${stops.length} arrêts, size=${size}`,
  );

  const {
    SPEED_SLOW_KMH,
    SPEED_MEDIUM_KMH,
    COLOR_SLOW,
    COLOR_MEDIUM,
    COLOR_FAST,
    COLOR_STOP,
    MAP_STYLE,
    STROKE_WIDTH,
  } = TRIP_MAP;

  // ── 1. Échantillonner à 200 pts (polyline encoded est compact, pas de risque URL) ─
  const MAX_LINE_PTS = 200;
  let linePts = positions;
  if (positions.length > MAX_LINE_PTS) {
    const step = (positions.length - 1) / (MAX_LINE_PTS - 1);
    const idx = new Set();
    for (let i = 0; i < MAX_LINE_PTS; i++) idx.add(Math.round(i * step));
    linePts = positions.filter((_, i) => idx.has(i));
  }

  const speedColor = (spd) => {
    if (spd <= SPEED_SLOW_KMH) return COLOR_SLOW;
    if (spd <= SPEED_MEDIUM_KMH) return COLOR_MEDIUM;
    return COLOR_FAST;
  };

  // ── 2. Grouper les segments contigus par couleur de vitesse ──────────────────
  const groups = [];
  let cur = null;
  for (let i = 0; i < linePts.length; i++) {
    const color = speedColor(linePts[i].speed ?? 0);
    if (!cur || cur.color !== color) {
      if (cur) groups.push(cur);
      cur = { color, pts: [linePts[i]] };
    } else {
      cur.pts.push(linePts[i]);
    }
    // Point-pont pour la continuité visuelle entre groupes
    if (i < linePts.length - 1 && speedColor(linePts[i + 1].speed ?? 0) !== color) {
      cur.pts.push(linePts[i + 1]);
    }
  }
  if (cur) groups.push(cur);

  // ── 3. Construire les overlays : paths polyline + marqueurs pin ──────────────
  const overlays = [];

  for (const { color, pts } of groups) {
    if (pts.length < 2) continue;
    overlays.push(`path-${STROKE_WIDTH}+${color}-0.9(${encodeURIComponent(encodePolyline(pts))})`);
  }

  const start = positions[0];
  overlays.push(`pin-l-bus+22c55e(${start.lng.toFixed(5)},${start.lat.toFixed(5)})`);

  stops.slice(0, 9).forEach((s, i) => {
    overlays.push(`pin-s-${i + 1}+${COLOR_STOP}(${s.lng.toFixed(5)},${s.lat.toFixed(5)})`);
  });

  const end = positions[positions.length - 1];
  overlays.push(`pin-l-school+ef4444(${end.lng.toFixed(5)},${end.lat.toFixed(5)})`);

  const url = [
    `https://api.mapbox.com/styles/v1/mapbox/${MAP_STYLE}/static`,
    `/${overlays.join(',')}`,
    `/auto/${size}@2x`,
    `?access_token=${MAPBOX_TOKEN}`,
    `&worldview=${MOROCCO_WORLDVIEW}`,
    `&padding=40`,
    `&attribution=false`,
    `&logo=false`,
  ].join('');

  console.debug(
    `[MAPBOX][DEBUG] URL générée — ${url.length} chars, ${overlays.length} overlays (${groups.length} segments couleur), token présent: ${!!MAPBOX_TOKEN && MAPBOX_TOKEN !== 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw'}`,
  );
  if (url.length > 8192) {
    console.warn(
      `[MAPBOX][DEBUG] ⚠️ URL encore trop longue (${url.length} chars) — vérifier le nombre de segments couleur`,
    );
  }

  return url;
};

// Styles de carte recommandés pour le Maroc
export const MOROCCO_MAP_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  light: 'mapbox://styles/mapbox/light-v11',
  dark: 'mapbox://styles/mapbox/dark-v11',
};

export default {
  MAPBOX_TOKEN,
  MOROCCO_WORLDVIEW,
  MOROCCO_CENTERS,
  createMapboxTransformRequest,
  DEFAULT_MAPBOX_CONFIG,
  createStaticMapUrl,
  createTripMapUrl,
  MOROCCO_MAP_STYLES,
};
